import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const DocumentViewer = ({ 
  contractData,
  highlights = [],
  onHighlightClick 
}) => {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Load PDF from localStorage
  useEffect(() => {
    if (contractData?.contract_filename) {
      const storedFileData = localStorage.getItem(`contract_file_${contractData.contract_filename}`);
      if (storedFileData) {
        setPdfUrl(storedFileData);
      }
    }
  }, [contractData]);

  const mockHighlights = [
    { id: 1, section: "Property Address", top: "15%", left: "20%", width: "60%", height: "3%" },
    { id: 2, section: "Closing Date", top: "35%", left: "15%", width: "40%", height: "2.5%" },
    { id: 3, section: "Purchase Price", top: "25%", left: "50%", width: "35%", height: "2.5%" },
    { id: 4, section: "Inspection Period", top: "45%", left: "25%", width: "45%", height: "2.5%" },
    { id: 5, section: "Financing Deadline", top: "55%", left: "20%", width: "50%", height: "2.5%" }
  ];

  return (
    <div className={`
      ${isFullscreen ? 'fixed inset-0 z-modal bg-card' : 'h-full'}
      flex flex-col border border-border rounded-lg overflow-hidden
    `}>
      {/* Document Viewer Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-3">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          <div>
            <h3 className="font-heading font-semibold text-foreground">
              {contractData?.contract_filename || 'Contract Document'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {contractData?.created_at ? new Date(contractData.created_at).toLocaleDateString() : 'No date available'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 bg-card border border-border rounded-lg p-1">
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel <= 50}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Zoom Out"
            >
              <Icon name="ZoomOut" size={16} color="var(--color-foreground)" />
            </button>
            
            <span className="px-2 text-sm font-mono text-foreground min-w-[3rem] text-center">
              {zoomLevel}%
            </span>
            
            <button
              onClick={handleZoomIn}
              disabled={zoomLevel >= 200}
              className="p-1.5 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              title="Zoom In"
            >
              <Icon name="ZoomIn" size={16} color="var(--color-foreground)" />
            </button>
            
            <button
              onClick={handleResetZoom}
              className="p-1.5 rounded hover:bg-muted transition-colors duration-200"
              title="Reset Zoom"
            >
              <Icon name="RotateCcw" size={16} color="var(--color-foreground)" />
            </button>
          </div>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            <Icon 
              name={isFullscreen ? "Minimize2" : "Maximize2"} 
              size={16} 
              color="var(--color-foreground)" 
            />
          </button>

          {isFullscreen && (
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
              title="Close"
            >
              <Icon name="X" size={16} color="var(--color-foreground)" />
            </button>
          )}
        </div>
      </div>

      {/* Document Content */}
      <div className="flex-1 overflow-auto bg-muted/10">
        {pdfUrl ? (
          <div className="h-full">
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              style={{ 
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'top left',
                minHeight: '600px'
              }}
              title="Contract PDF"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-center p-8">
            <div>
              <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">PDF Not Available</h3>
              <p className="text-muted-foreground">
                The original PDF file is not available for viewing. 
                <br />
                Upload a new contract to enable PDF preview.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Highlight Legend */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center space-x-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-accent/20 border border-accent rounded"></div>
            <span className="text-muted-foreground">Highlighted sections</span>
          </div>
          <div className="text-muted-foreground">
            Click highlighted areas to view extracted data
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;